import { Component, Input, OnInit, HostListener } from '@angular/core';
import { CatalogService } from '../catalog-api/catalog.service';
import { ToasterService } from 'angular2-toaster';
import { DPTitleBatchRequest } from '../../portalv1/model/dp-title-batch-request';
import { Modal } from 'ngx-modialog/plugins/bootstrap/';
import { DPSubscriptionService } from '../../portalv1';
import { QAService } from '../qa/qa.service';
import { UserDetailsService } from '../../shared/user.details.service';
import { TitleRequests } from '../catalog-api/model/title-requests';
import { GeneralConstants } from '../../shared/general.constants';

@Component({
    selector: 'bulk-actions-dropdown',
    templateUrl: 'bulk-actions-dropdown.component.html',
    styleUrls: ['bulk-actions-dropdown.component.scss']
})
export class BulkActionsDropdownComponent {
    @Input() results;

    // Booleans for displaying elements
    private bulkActionsLoading = false;
    private bulkActionsCollapsed = false;

    // Title requests array
    private titleRequests: TitleRequests[];

    // Titles to add array
    private titlesToAdd: any[];

    constructor(private catalogService: CatalogService,
        private toasterService: ToasterService,
        private dpSubscriptionsService: DPSubscriptionService,
        public modal: Modal,
        private qaService: QAService,
        private userDetailsService: UserDetailsService,
        private generalConstants: GeneralConstants) {}

    clearSelected() {
        this.results = this.results.map((item) => {
            item.checked = false;
            return item;
        });
    }

    @HostListener('document:click', ['$event'])
    public documentClick(event: any) {
        if (event.target && event.target.innerText !== 'Bulk Actions ' && this.bulkActionsCollapsed) {
            this.bulkActionsCollapsed = false;
        }
    }

    handleBulkAction(actionType) {
        this.bulkActionsLoading = true;
        this.bulkActionsCollapsed = false;
        // Get all titles that have been selected.
        // Remove titles without catalog states if not a title request action.
        // If a title is empty, create an empty title request with the title name.
        const selectedTitles = this.results.filter(item => (item.checked || (item.empty && actionType === 'title_request')) &&
            ((actionType !== 'title_request' && item.catalogState) || actionType === 'title_request'));

        // Short circuit validation before performing the action.
        if (selectedTitles.length === 0) {
            this.toasterService.pop('info', 'No valid titles have been selected!');
            this.bulkActionsLoading = false;
        } else if (!this.userDetailsService.isAdmin() && actionType !== 'title_request') {
            // If the action is not title_request, check that the user is a helpdesk admin
            this.toasterService.pop('error', 'You do not have permission to perform this action!');
            this.bulkActionsLoading = false;
        } else {
            const user = localStorage.getItem('userName');
            switch (actionType) {
                case 'mappings_qa':
                    // Create a QA request for mappings
                    Promise.all(
                        selectedTitles.map((item) => {
                                return new Promise((resolve, reject) => {
                                    this.qaService.createQARequest(item.parrotId, user, 'mappings')
                                        .then((res) => {
                                            if (res.message && res.message === 'Success') {
                                                resolve(1);
                                            } else {
                                                resolve(0);
                                            }
                                        })
                                        .catch((err) => {
                                            const error = JSON.parse(err._body);
                                            if (error.error && error.error.message && error.error.message === this.generalConstants.getErrorQaAlreadyExistsMessage()) {
                                                resolve(2);
                                            } else {
                                                console.error('Failed to create QA requests: ' + err.toString());
                                                resolve(0);
                                            }
                                        });
                                });
                        })
                    )
                    .then((res) => {
                        this.clearSelected();
                        this.bulkActionsLoading = false;
                        const successfullyAdded = res.filter((item) => item === 1 || item === 2).length;
                        const unsuccessfullyAdded = res.filter((item) => item === 0).length;
                        const totalAdded = successfullyAdded + unsuccessfullyAdded;
                        if (successfullyAdded === totalAdded) {
                            this.toasterService.pop('success', `Successfully created ${successfullyAdded} mappings QA requests`);
                        } else if (successfullyAdded > 0) {
                            this.toasterService.pop('info', `Successfully created ${successfullyAdded} mappings QA requests.` +
                                ` Failed to create ${unsuccessfullyAdded} mappings QA requests.`);
                        } else {
                            this.toasterService.pop('error', 'Failed to create mappings QA requests');
                        }
                    });
                    break;
                case 'images_qa':
                    // Create a QA request for mappings
                    Promise.all(
                        selectedTitles.map((item) => {
                            return new Promise((resolve, reject) => {
                                this.qaService.createQARequest(item.parrotId, user, 'images')
                                    .then((res) => {
                                        if (res.message && res.message === 'Success') {
                                            resolve(1);
                                        } else {
                                            resolve(0);
                                        }
                                    })
                                    .catch((err) => {
                                        const error = JSON.parse(err._body);
                                        if (error.error && error.error.message && error.error.message === this.generalConstants.getErrorQaAlreadyExistsMessage()) {
                                            resolve(2);
                                        } else {
                                            console.error('Failed to create QA requests: ' + err.toString());
                                            resolve(0);
                                        }
                                    });
                            });
                        })
                    )
                    .then((res) => {
                        this.clearSelected();
                        this.bulkActionsLoading = false;
                        const successfullyAdded = res.filter((item) => item === 1 || item === 2).length;
                        const unsuccessfullyAdded = res.filter((item) => item === 0).length;
                        const totalAdded = successfullyAdded + unsuccessfullyAdded;
                        if (successfullyAdded === totalAdded) {
                            this.toasterService.pop('success', `Successfully created ${successfullyAdded} images QA requests`);
                        } else if (successfullyAdded > 0) {
                            this.toasterService.pop('info', `Successfully created ${successfullyAdded} images QA requests.` +
                                ` Failed to create ${unsuccessfullyAdded} images QA requests.`);
                        } else {
                            this.toasterService.pop('error', 'Failed to create images QA requests');
                        }
                    });
                    break;
                case 'priority':
                    // Gene merge priority
                    const parrotIds = selectedTitles.map((item) => item.parrotId);
                    const contentType = selectedTitles.map((item) => item.contentType);

                    this.catalogService.updateGenome(
                        parrotIds, Array(parrotIds.length).fill('priority'), Array(parrotIds.length).fill('high'), contentType
                    )
                        .then((res: any) => {
                            this.bulkActionsLoading = false;
                            this.clearSelected();
                            if (res && res.message) {
                                this.toasterService.pop('success',
                                'Successfully updated the priority for the selected titles');
                            } else {
                                console.error('Error updating the priority for the selected titles: '
                                    + JSON.stringify(res));
                                this.toasterService.pop('error',
                                'Failed to update the priority for the selected titles');
                            }
                        })
                        .catch((err) => {
                            this.bulkActionsLoading = false;
                            this.clearSelected();
                            console.error('Error updating the priority for the selected titles: '
                                    + err.toString());
                            this.toasterService.pop('error',
                                'Failed to update the priority for the selected titles');
                        });
                    break;
                case 'daily_infohash_check':
                    // Call add to daily infohash endpoint
                    Promise.all(selectedTitles.map((item) =>  {
                        return new Promise((resolve, reject) => {
                            this.catalogService.addToDailyInfohashes(item.parrotId)
                                .then((res) => {
                                    resolve(1);
                                })
                                .catch((err) => {
                                    const error = JSON.parse(err._body);
                                    if (error.error && error.error.message && error.error.message === this.generalConstants.getErrorInfohashExistsMessage()) {
                                        resolve(2);
                                    } else {
                                        console.error('Failed to add to daily infohashes for title ' + item.parrotId);
                                        resolve(0);
                                    }
                                });
                        });
                    }))
                    .then((res) => {
                        this.clearSelected();
                        const successfullyAdded = res.filter((item) => item === 1).length;
                        const alreadyAdded = res.filter((item) => item === 2).length;
                        if (successfullyAdded === res.length) {
                            this.toasterService.pop('success', `Successfully added ${successfullyAdded} titles to the daily infohash check`);
                        } else if (successfullyAdded > 0 || alreadyAdded > 0) {
                            this.toasterService.pop('info', `Successfully added ${successfullyAdded} titles to the daily infohash check.` +
                                ` Already added ${alreadyAdded} titles.` +
                                ` Failed to add for ${res.length - successfullyAdded - alreadyAdded} titles`);
                        } else {
                            this.toasterService.pop('error', 'Failed to add titles to the daily infohash title check');
                        }
                        this.bulkActionsLoading = false;
                    });
                    break;
                case 'add_to_account':
                    // Ask the user if they are sure they want to set the titles to customer ready
                    this.titlesToAdd = selectedTitles;
                    this.bulkActionsLoading = false;
                    break;
                case 'title_request':
                    this.titleRequests = selectedTitles.filter((item) => !item.catalogState).map((item) => {
                        let link;
                        if (item.imdbId) {
                            // Create imdb link
                            link = `https://www.imdb.com/${item.imdbId.includes('tt') ? 'title' : 'name'}/${item.imdbId}`;
                        } else if (item.externalMappings) {
                            // Create a link from external mappings with imdb first then wiki
                            if (item.extenalMappings.imdb_id) {
                                link = `https://www.imdb.com/${item.imdbId.includes('tt') ? 'title' : 'name'}/${item.imdbId}`;
                            } else if (item.extenalMappings.wiki_id) {
                                link = `https://en.wikipedia.org/w/index.php?curid=${item.wiki_id}`;
                            }
                        } else {
                            link = '';
                        }
                        return {
                            parrotId: item.parrotId ? item.parrotId : null,
                            title: item.displayName,
                            link,
                            country: item.originalCountry ? item.originalCountry : null,
                            year: item.releaseYear ? item.releaseYear : null,
                            network: item.originalNetwork ? item.originalNetwork : null
                        } as TitleRequests;
                    });
                    this.bulkActionsLoading = false;
                    break;
                default:
                    this.bulkActionsLoading = false;
                    this.clearSelected();
                    this.toasterService.pop('error', `Bulk action ${actionType} is not handled`);
                    break;
            }
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/catalogue/bulk-actions-dropdown/bulk-actions-dropdown.component.ts